import { apiLogger } from '@/config/logging';
import { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

/**
 * Dev-only escape hatch for testing the offline service worker: with the
 * in-memory query cache disabled every mount refetches through the SW, so
 * what you see is the SW cache, not TanStack's. Toggle from the console:
 *
 *   serv.queryCache.disable()  // zero stale/gc time + clears the cache
 *   serv.queryCache.enable()   // restore normal caching
 *
 * Persisted in localStorage so it survives (online) reloads.
 */
const DISABLE_QUERY_CACHE_KEY = 'certhon:disable-query-cache';

function isQueryCacheDisabled(): boolean {
  try {
    return (
      import.meta.env.DEV &&
      localStorage.getItem(DISABLE_QUERY_CACHE_KEY) === 'true'
    );
  } catch {
    return false;
  }
}

const CACHED_TIMES = {
  staleTime: 1000 * 60 * 5, // 5 minutes
  gcTime: 1000 * 60 * 10, // 10 minutes
};
const UNCACHED_TIMES = { staleTime: 0, gcTime: 0 };

/**
 * The service worker is the offline layer: fetches must ALWAYS fire so the
 * SW can serve from its caches (queries) and queue events (mutations).
 * TanStack's default networkMode 'online' would pause everything the moment
 * navigator.onLine goes false — the request would never reach the SW.
 */
const NETWORK_MODE = { networkMode: 'always' as const };

export function setQueryCacheDisabled(disabled: boolean): void {
  localStorage.setItem(DISABLE_QUERY_CACHE_KEY, String(disabled));
  queryClient.setDefaultOptions({
    queries: {
      retry: false,
      throwOnError: true,
      ...NETWORK_MODE,
      ...(disabled ? UNCACHED_TIMES : CACHED_TIMES),
    },
  });
  queryClient.clear();
  apiLogger.info(`TanStack query cache ${disabled ? 'DISABLED' : 'enabled'}`);
}

// Create singleton QueryClient instance
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      throwOnError: true,
      ...NETWORK_MODE,
      ...(isQueryCacheDisabled() ? UNCACHED_TIMES : CACHED_TIMES),
    },
    mutations: {
      ...NETWORK_MODE,
      onError: (error) => {
        apiLogger.error('Mutation error: {error}', { error });
        toast.error('Mutation error', {
          description: error.message,
        });
      },
    },
  },
});

// const _invalidateQueries = queryClient.invalidateQueries;
// (queryClient as any).invalidateQueries = function (...args: any[]) {
//   apiLogger.info('invalidate {args}', { args });
//   _invalidateQueries.call(queryClient, ...args);
// };
